import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HikingIcon from '@mui/icons-material/Hiking';
import DiamondIcon from '@mui/icons-material/Diamond';
import PetsIcon from '@mui/icons-material/Pets';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';

const ExploreThemes = () => {
  const navigate = useNavigate();

  const themes = [
    {
      id: 1,
      title: 'Spiritual',
      subtitle: 'Explore the serene temples and divine vibes.',
      icon: <TempleHinduIcon sx={{ fontSize: 32 }} />,
      image: 'https://images.unsplash.com/photo-1705952484283-19c31e37e0e4?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8dmFyYW5hc2l8ZW58MHx8MHx8fDA%3D',
      route: '/tours?category=Religious',
    },
    {
      id: 2,
      title: 'Honeymoon',
      subtitle: 'Celebrate love with dreamy honeymoon getaways',
      icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
      image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&w=800&q=80',
      route: '/tours?search=Honeymoon',
    },
    {
      id: 3,
      title: 'Luxury',
      subtitle: 'Indulge in premium experiences and world-class amenities',
      icon: <DiamondIcon sx={{ fontSize: 32 }} />,
      image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=800&q=80',
      route: '/tours?search=Luxury',
    },
    {
      id: 4,
      title: 'Adventure',
      subtitle: 'Thrilling activities for the adrenaline junkies',
      icon: <HikingIcon sx={{ fontSize: 32 }} />,
      image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=800&q=80',
      route: '/tours?search=Adventure',
    },
    {
      id: 5,
      title: 'Wildlife',
      subtitle: 'Explore nature and spot exotic animals in their habitat',
      icon: <PetsIcon sx={{ fontSize: 32 }} />,
      image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=800&q=80',
      route: '/wildlife-tours',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fafafa' }}>
      <Container maxWidth="lg">
        {/* Simple Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Explore Themes that Inspire Travel
          </Typography>
        </Box>

        {/* Simple Grid */}
                <Grid container spacing={{ xs: 2, sm: 3 }}>
          {themes.map((item) => (
            <Grid item xs={12} sm={6} md={3} lg={2.4} key={item.id}>
              <Card
                onClick={() => navigate(item.route)}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  height: { xs: 320, sm: 380 },
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.12)',
                    '& .theme-img': { transform: 'scale(1.05)' },
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardMedia
                  className="theme-img"
                  component="img"
                  height="380"
                  image={item.image}
                  alt={item.title}
                  sx={{
                    transition: 'transform 0.5s ease',
                    height: { xs: 320, sm: 380 },
                  }}
                />
                
                {/* Dark overlay at bottom only */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '60%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Content */}
                <CardContent
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    color: 'white',
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ color: '#ffc107', mb: 1 }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      mb: 2,
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      borderRadius: 50,
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      px: 2.5,
                      py: 0.6,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.15)',
                      },
                    }}
                  >
                    Explore
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ExploreThemes;